import { Component, effect, inject } from '@angular/core';
import { Binder } from 'src/app/models/binder.model';
import { UserData } from 'src/app/models/user-data.model';
import { MessageService } from 'primeng/api';
import { DocDataService } from 'src/app/services/app/doc-data.service';
import { UserAccessService } from 'src/app/services/app/user-access.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { BinderClientsComponent } from '../../components/binder.clients.component';
import { RecipientsComponent } from '../recipients/recipients.component';
import { ButtonModule } from 'primeng/button';
import { masterJson } from '@/models/master-json.model';
import { SyncStatus } from '@/models/sync-status.model';
import { FileCategory } from '@/models/binder-file';
import { SigRequest, SigResult } from '@/models/sig-request.model';
import { BinderService } from '@/services/api/binder.service';
import { UsersService } from '@/services/api/users.service';
import { SignaturesService } from '@/services/api/signatures.service';
import { DocGeneratorService } from '@/services/api/doc-generator.service';
import { BinderDialogService } from '@/services/app/binder-dialog.service';
import { FluidModule } from 'primeng/fluid';
import { OfficesService } from '@/services/api/franchises.service';
import { DocStatusService } from '@/services/app/doc-status.service';
import { DocSyncStatus } from '@/models/doc-sync-status.model';
import { FilesService } from '@/services/api/files.service';
import { SignatureClientsComponent } from './client-card.component';
import { ClientHybrid } from '@/models/client.model';
import { ClientService } from '@/services/api/client.service';
import { ApprovalsService } from '@/services/api/approvals.service';

@Component({
    selector: 'app-signature-request',
    imports: [
        CommonModule,
        FormsModule,
        SelectModule,
        InputTextModule,
        SignatureClientsComponent,
        RecipientsComponent,
        ButtonModule,
        FluidModule
    ],
    template: `
  <div class="flex flex-col md:flex-row gap-4">
    <div class="md:w-1/2">
    <div class="font-semibold text-xl">Signers</div>
    <app-signature-clients 
    *ngFor="let client of clientDataService.clientData()"
    [emailItems]="client.emailItems" 
    [selectedEmail]="client.selectedEmail || null" 
    [mode]="client.mode || 'email'" 
    [modeDisabled]="false" 
    [inputDisabled]="false" 
    [name]="client.ClientFirstName + ' ' + client.ClientLastName" 
    [subtitle]="getLabels(client)" 
    (modeChange)="updateMode($event, client)" 
    (emailChange)="updateEmail($event, client)"/>

    <app-signature-clients 
    [emailItems]="builderEmails" 
    [selectedEmail]="builderEmail" 
    [mode]="mode" 
    [modeDisabled]="true" 
    (emailChange)="updateBuilderEmail($event)"
    [inputDisabled]="false" 
    [name]="'Builder'" 
    [subtitle]="'Signing for office'"></app-signature-clients>

    <app-signature-clients 
    [emailItems]="[{email: corpEmail}]" 
    [selectedEmail]="corpEmail" [mode]="mode" 
    [modeDisabled]="true" 
    [inputDisabled]="true" 
    [name]="'James Moen'" 
    [subtitle]="'Signing for corporate'"></app-signature-clients>


    </div>
    <div class="md:w-1/2">
      <div class="flex flex-col gap-4">
        <div class="font-semibold text-xl">Recipients</div>
        <div class="flex flex-col gap-2">
        <app-recipients></app-recipients>
        </div>
      </div>
    </div>
  </div>

  <div class="flex mt-8">
    <div class="flex flex-col gap-4 w-full footer-buttons">
      <div class="flex flex-wrap justify-end">
        <div class="info-action-row">
          <div class="action-row">
            <div class="action-item">
              <p-button icon="pi pi-file-pdf" [loading]="loading" [rounded]="false" [label]="'Request Signatures'" (click)="requestSignatures()" [disabled]="!builderEmail"></p-button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
    `,
    styles: [
      `
.card {
    margin-top: 4px;
    margin-bottom: 4px;
    margin-left: 0px;
    margin-right: 0px;
}

.conTab {
    padding: 0px;
}

.conTable {
    padding: 0px;
}

#conIcon {
    margin-left: 0px;
    width: 40px;
    padding: 0px;
}
.info-action-row {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
}

.action-row {
    display: flex;
    justify-content: flex-end;
    width: 100%;
}

.action-item {
    margin-right: 0;
    margin-top: 5px;
    margin-bottom: 5px;
}

.footer-buttons {
    display: flex;
    justify-content: flex-end;
    position: sticky;
    bottom: 0;
    padding: 1em;
    width: 100%;
}
      `
    ]
})
export class SignatureRequestComponent {
  currentBinderService = inject(BinderService);
  docDataService = inject(DocDataService);
  userListService = inject(UsersService);
  signatureService = inject(SignaturesService);
  generatorService = inject(DocGeneratorService);
  filesService = inject(FilesService);
  messageService = inject(MessageService);
  binderDialogService = inject(BinderDialogService);
  userAccessService = inject(UserAccessService);
  officeService = inject(OfficesService);
  clientDataService = inject(ClientService);
  docSyncStatusService = inject(DocStatusService);
  approvalsService = inject(ApprovalsService);
  modes = [ {label: 'Email', value: 'email'}, {label: 'In-Person', value: 'in-person'}];
  mode = 'email';

  masterJSON: masterJson | null = null;
  userList!:UserData[];
  binder!:Binder;
  loading: boolean = false;
  builderEmail!:string;
  corpEmail!:string;
  builderEmails!:{email:string}[];
  selectedRecipients!: {name:string,email:string}[];
  
  private defaultEmailSet = false; // Flag to prevent loop

  response: SigResult = new SigResult();
  approval: boolean = false;

  constructor(
    
    ) {
      if(this.officeService.offices().length === 0) {
      this.userListService.populateUserList();
      }
      if(this.officeService.offices().length === 0) {
        this.officeService.fetchOffices();
      }
      effect(() => {
        const binder = this.currentBinderService.currentBinder();
        const json = this.docDataService.jsonData();
        const userList = this.userListService.userList();
        const offices = this.officeService.offices();

        if(binder && userList) {
          this.binder = binder;
          this.userList = userList;
          this.filterBuilderEmails(binder.SelectedFID,userList);
        }
        if(json) {
          this.masterJSON = json;
          this.builderEmail = json?.peopleList.find(person => person.clientId === 1)?.email || '';
          this.corpEmail = json?.peopleList.find(person => person.clientId === 0)?.email || '';
          console.log('masterJSON changed');
          const inPerson = json?.peopleList.some(person => person.mode === 'in-person') || false;
          console.log('inPerson:',inPerson)
        }
        // Only set default email once when all dependencies are available and it hasn't been set yet
        if(offices && offices.length > 0 && binder && userList && !this.defaultEmailSet && (!json || !this.builderEmail)) {
          this.setDefaultBuilderEmail();
          this.defaultEmailSet = true;
        }
      });
    }

get syncStatus(): DocSyncStatus {
  return this.docSyncStatusService.syncStatus();
}

get files(): FileCategory[] {
  return this.filesService.categories().filter(category => category.archived !== true);
}

get isApproved(): boolean { 
  return this.approvalsService.getComputedApprovalState() === 'approved';
}

setSelectedRecipients(recipients:{name:string,email:string}[]) {
  this.selectedRecipients = recipients;
}

getLabels(client:ClientHybrid) {
  return client.labels?.map(label => label.label).join(', ') || '';
}

filterBuilderEmails(fid:number,data:UserData[]) {
    this.builderEmails = this.userList
      .filter(user => 
        user.UserRoles?.some(role => 
          role.PermRoles?.RoleAccess?.some(access => 
            access.AccessPermissions.dataTable === 'Binders' && (access.AccessPermissions.FID === fid || access.AccessPermissions.FID === 0) && access.AccessPermissions.permType === 'sign'
          )
        )
      )
      .map(user => ({
        email: user.User || ''
      }))
      .filter(email => email.email);
}

setDefaultBuilderEmail() {
const selected = this.officeService.offices().find(office => office.FID === this.binder.SelectedFID);
const main = this.officeService.offices().find(office => office.FID === selected?.ParentFID);
const builderId = selected?.DefaultBuilder || main?.DefaultBuilder;
console.log('builderId:',builderId);

if(builderId) {
  const builder = this.userList.find(user => user.UID === builderId);
  if(builder && builder.User) {
    this.builderEmail = builder.User;
    // this.builderEmail = 'spexisbackend@lexarhomes.com'
    // Only update the doc service if the email isn't already set to prevent loops
    const currentBuilderEmail = this.masterJSON?.peopleList.find(person => person.clientId === 1)?.email;
    if (!currentBuilderEmail) {
      this.updateBuilderEmail(this.builderEmail);
    }
  }
}
}

async requestSignatures() {
  this.loading = true
  let json = this.docDataService.jsonData()

  this.updateBuilderEmail(this.builderEmail);
  if (!this.syncStatus.signReady && !this.isApproved) {
    await this.generatorService.generateSelected(); 
  }
  console.log('sign request json:',json)
  if(json){
  const signers = json.peopleList
  const inPerson = signers?.some(signer => signer.mode === 'in-person') || false;
  console.log('files:',this.files)
  const files = this.files.filter(file => file.fileCategoryId === 1 || file.fileCategoryId === 2 && file.binderFiles).map(file => ({name:file.binderFiles![0].fileName, path:file.binderFiles![0].blobUrl}))
  const request:SigRequest = {
    binderId:this.binder.BinderID,
    inPerson:inPerson,
    embedded_signing_enabled:inPerson,
    use_signer_order:!inPerson,
    files:files,
    signers:signers,
    recipients:this.selectedRecipients || []
  }
  console.log('signature request:', request);
  this.signatureService.requestSignatures(request).subscribe(
    (response) => {
      console.log('response:', response);
      this.response = response as SigResult;
      console.log('response:', response);
      this.messageService.add({severity:'success', summary: 'Success', detail: 'Signatures requested', life: 3000});
      this.userAccessService.setBinderEditAccess(false);
      if(this.response.eversignResponse.document_hash) {
        this.currentBinderService.patchBinder({ES_Hash: this.response.eversignResponse.document_hash, ProjectStatus: 7});
      }
      this.loading = false;
      this.binderDialogService.closeDialog(true);
    },
    error => {
      // console.error('Error requesting signatures:', error.error.error.message);
      const info = error?.error?.error?.message || 'Unknown error';
      this.messageService.add({severity:'error', summary: 'Error', detail: `Error requesting signatures: ${info}`, life: 8000});
      this.loading = false;
    }

  );
}
else {
  this.messageService.add({severity:'error', summary: 'Error', detail: 'No document data found to request signatures', life: 8000});
  this.loading = false;
}
}

updateBuilderEmail(email:string) {
  console.log('updating builder email:',email)
  const user = this.userList.find(user => user.User === email);
  this.docDataService.updateSelectedEmail(1,email,user?.displayName || user?.DisplayName || user?.givenName + " " + user?.surname || '');
}

updateEmail(email:string,client:ClientHybrid){
  const clientId = client.clientId;
  const displayName = client.ClientFirstName + " " + client.ClientLastName;
  if(clientId){
    this.clientDataService.updateSelectedEmail(clientId,email);
    this.docDataService.updateSelectedEmail(clientId,email, displayName || '');
  } else {
    console.log("No clientId provided")
  }
}

updateMode(mode:string,client:ClientHybrid){
  console.log('updating mode:',mode,client)
  const clientId = client.clientId;
  if(clientId){
    this.clientDataService.updateSelectedMode(clientId,mode);
    this.docDataService.updateSelectedMode(clientId,mode);
  } else {
    console.log("No clientId provided")
  }
}

}
